import React, { useState } from 'react';
import { Box, Button, Typography } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useDispatch } from 'react-redux';
import BuzzInput from '../../../components/BuzzInput/BuzzInput';
import useToast from '../../../hooks/useToast';
import useLoader from '../../../hooks/useLoader';
import { updateFlagRelease } from '../../Dashboard/FeatureManagement/featureFlagsSlice';
import { severityColour } from '../../Dashboard/FeatureManagement/flagDisplay';
import { normaliseId, validateObjectId } from './objectId';
import { isProductionEnv } from '../../../environments';
import { findWithAncestors, thunkErrorMessage } from './useFlagTree';

const FLAG_KEY = 'website_builder.legacy_school_fallback';

/**
 * Adds one school to the legacy-school fallback flag's pilot list.
 *
 * buzzone-api's release PUT *replaces* `schoolIds` and accepts any status transition,
 * including ALL → PARTIAL, so this screen must never send a list or status it did not
 * just read. Anything beyond "add a school to PARTIAL" (CANARY, re-enabling a stored
 * list) is a release decision and is left to Feature Management.
 *
 * The flag is resolved against the *site owner's* Teenybeans school (`website.schoolId`),
 * not the legacy school a site is linked to.
 */

const idsOf = (row) => ((row && row.schoolIds) || []).map(normaliseId);

// Mirrors the backend's `selfEnabled` for a school, minus the per-user overlay, which a
// school context never reaches. CANARY buckets on a server-side hash we do not reproduce.
const schoolSees = (row, schoolId) => {
  switch (row.releaseStatus) {
    case 'ALL':
      return true;
    case 'PARTIAL':
      return idsOf(row).includes(schoolId);
    case 'CANARY':
      return 'bucket';
    default:
      return false;
  }
};

/** → `{ enabled: true | false | null, text }`; null when it depends on a canary bucket. */
const describeForSchool = (found, schoolId) => {
  if (!found) {
    return { enabled: false, text: `Not enabled: no "${FLAG_KEY}" flag row.` };
  }

  // Ancestors gate root-down and the first blocker wins, as in the backend.
  for (const { key, node } of found.ancestors) {
    const row = node.row || {};
    if (row.availability && row.availability !== 'LIVE') {
      return {
        enabled: false,
        text: `Not enabled: parent ${key} is ${row.availability}.`,
      };
    }
    const seen = schoolSees(row, schoolId);
    if (seen === 'bucket') {
      return {
        enabled: null,
        text: `Depends on parent ${key}'s CANARY bucket (${
          row.rolloutPct || 0
        }%).`,
      };
    }
    if (!seen) {
      return {
        enabled: false,
        text: `Not enabled: blocked by parent ${key} (${row.releaseStatus}${
          row.releaseStatus === 'PARTIAL' ? ' without this school' : ''
        }).`,
      };
    }
  }

  const row = found.node.node.row || {};
  if (row.availability && row.availability !== 'LIVE') {
    return {
      enabled: false,
      text: `Not enabled: the flag is ${row.availability}.`,
    };
  }
  const seen = schoolSees(row, schoolId);
  if (seen === 'bucket') {
    return {
      enabled: null,
      text: `Depends on the CANARY rollout bucket (${row.rolloutPct || 0}%).`,
    };
  }
  return seen
    ? { enabled: true, text: 'Enabled for this school.' }
    : { enabled: false, text: 'Not enabled for this school.' };
};

/**
 * The flag's state for everyone, for the banner → `{ severity, text, allClear }`.
 * `allClear` (ALL, LIVE, nothing above it gating) means there is nothing for Step 1 to do.
 *
 * Parent gating comes from the tree's own `blockedByAncestor` / `narrowedByAncestor`, which
 * the backend computes — this only describes them.
 */
const describeOverall = (found) => {
  if (!found) {
    return {
      severity: 'error',
      text: `No "${FLAG_KEY}" flag row exists.`,
      allClear: false,
    };
  }
  const { blockedByAncestor, narrowedByAncestor } = found.node;
  const parentRow = (key) => {
    const parent = found.ancestors.find((ancestor) => ancestor.key === key);
    return (parent && parent.node.row) || {};
  };

  if (blockedByAncestor) {
    const parent = parentRow(blockedByAncestor);
    const state =
      parent.availability && parent.availability !== 'LIVE'
        ? parent.availability
        : parent.releaseStatus;
    return {
      severity: 'warning',
      text: `Blocked by parent ${blockedByAncestor} (${state}): off for every school whatever is set here.`,
      allClear: false,
    };
  }

  const row = found.node.node.row || {};
  if (row.availability && row.availability !== 'LIVE') {
    return {
      severity: 'warning',
      text: `Flag is ${row.availability}: off for every school until it is restored in Feature Management.`,
      allClear: false,
    };
  }
  if (narrowedByAncestor) {
    const parent = parentRow(narrowedByAncestor);
    return {
      severity: 'warning',
      text: `Parent ${narrowedByAncestor} is at ${parent.releaseStatus}: only schools it reaches can see this flag.`,
      allClear: false,
    };
  }

  switch (row.releaseStatus) {
    case 'ALL':
      return {
        severity: 'success',
        text: 'Enabled for all schools. No action needed, go to Step 2.',
        allClear: true,
      };
    case 'PARTIAL': {
      const count = idsOf(row).length;
      return {
        severity: 'info',
        text: `Enabled for ${count} school${count === 1 ? '' : 's'}.`,
        allClear: false,
      };
    }
    case 'CANARY':
      return {
        severity: 'warning',
        text: `Flag is at CANARY (${
          row.rolloutPct || 0
        }%): enabling a single school here is blocked. Use Feature Management.`,
        allClear: false,
      };
    default:
      return { severity: 'warning', text: 'Flag is OFF.', allClear: false };
  }
};

// Adding a school changes the production flag for that school, a release decision that
// belongs to Feature Management there. The banner still reports the real state.
const READ_ONLY = isProductionEnv();

// `canManage`, `email`, `tree` and `reload` come from the page's shared useFlagTree.
const EnableLegacySchoolFallback = ({ canManage, email, tree, reload }) => {
  const dispatch = useDispatch();
  const { ErrorToast, SuccessToast, WarningToast } = useToast();
  const { handleLoader } = useLoader();

  const [schoolId, setSchoolId] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!canManage) {
    return (
      <Typography variant="body2" color="textSecondary">
        Enabling the legacy-school fallback needs feature-flag access
        (bccSuperAdmin).
      </Typography>
    );
  }

  const overall = tree
    ? describeOverall(findWithAncestors(tree, FLAG_KEY))
    : null;
  const allClear = !!(overall && overall.allClear);
  const formLocked = allClear || READ_ONLY;

  const normalisedId = normaliseId(schoolId);
  const status =
    tree && !validateObjectId(normalisedId)
      ? describeForSchool(findWithAncestors(tree, FLAG_KEY), normalisedId)
      : null;

  const handleChange = (e) => {
    setSchoolId(e.target.value);
    setError('');
    setInfo('');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Guarded here too, not only by the disabled form: Enter in the input submits it.
    if (submitting || READ_ONLY) return;

    const id = normalisedId;
    const idError = validateObjectId(id);
    setError(idError);
    setInfo('');
    if (idError) return;

    const reason = `Enabled for school ${id} from Teenybeans Portal → Website${
      email ? ` by ${email}` : ''
    }`;

    setSubmitting(true);
    handleLoader(true);

    // Re-read right before writing: the PUT replaces the list, so merging into a copy
    // fetched at page load could drop a school someone added since.
    reload()
      .catch((body) => {
        throw new Error(
          thunkErrorMessage(body, 'Could not load feature flags')
        );
      })
      .then((freshTree) => {
        const found = findWithAncestors(freshTree, FLAG_KEY);
        if (!found) throw new Error(`No "${FLAG_KEY}" flag row exists.`);
        const row = found.node.node.row || {};
        const currentIds = idsOf(row);

        if (
          row.releaseStatus === 'ALL' ||
          (row.releaseStatus === 'PARTIAL' && currentIds.includes(id))
        ) {
          setInfo('Already enabled for this school.');
          return null;
        }
        if (row.releaseStatus === 'CANARY') {
          WarningToast(
            `The flag is at CANARY (${
              row.rolloutPct || 0
            }%) — switching to PARTIAL would remove canary schools. Change it in Feature Management.`
          );
          return null;
        }
        // An OFF flag keeps its old pilot list; merging into it would quietly re-enable
        // every school on it.
        if (row.releaseStatus === 'OFF' && currentIds.length) {
          WarningToast(
            `The flag is OFF with ${currentIds.length} previously listed school(s) — enabling here would re-enable them. Use Feature Management.`
          );
          return null;
        }

        const schoolIds = [...currentIds, id];
        // Only the fields we mean to change: the backend leaves omitted fields (the
        // per-user overlay, rolloutPct) untouched.
        const data = { releaseStatus: 'PARTIAL', schoolIds, reason };
        return dispatch(updateFlagRelease({ key: FLAG_KEY, data }))
          .unwrap()
          .catch((body) => {
            throw new Error(
              thunkErrorMessage(body, 'Could not update the feature flag')
            );
          })
          .then((res) => {
            const saved = idsOf(res && res.row);
            const missing = schoolIds.filter((sent) => !saved.includes(sent));
            if (
              !res ||
              !res.row ||
              res.row.releaseStatus !== 'PARTIAL' ||
              missing.length
            ) {
              WarningToast(
                `Saved, but the flag does not list ${missing.length} of the schools sent. Check it in Feature Management.`
              );
              return reload();
            }
            return reload().then((latest) => {
              const after = describeForSchool(
                findWithAncestors(latest, FLAG_KEY),
                id
              );
              if (after.enabled)
                SuccessToast('Feature enabled for this school.');
              else
                WarningToast(`Saved, but still not effective. ${after.text}`);
            });
          });
      })
      // Errors raised above are Errors; a failed post-write reload rejects with a raw body.
      .catch((err) =>
        ErrorToast(
          err instanceof Error
            ? err.message
            : thunkErrorMessage(err, 'Could not reload feature flags')
        )
      )
      .finally(() => {
        setSubmitting(false);
        handleLoader(false);
      });
  };

  return (
    <Box>
      {overall && (
        <Box mb={2}>
          <Alert severity={overall.severity}>{overall.text}</Alert>
        </Box>
      )}
      <Typography variant="h6" component="div">
        <Box fontWeight="fontWeightMedium">
          Enable the legacy-school fallback
        </Box>
      </Typography>
      <Typography variant="body2" color="textSecondary">
        Turns on {FLAG_KEY} for one school. Use the site owner's Teenybeans
        school ID (the id in its Schools → View link), not the legacy school.
      </Typography>
      <form onSubmit={handleSubmit} noValidate>
        <BuzzInput
          id="tb-website-flag-school-id"
          name="schoolId"
          label="School ID"
          required
          mt={3}
          value={schoolId}
          disabled={formLocked}
          onChange={handleChange}
          error={!!error}
          helperText={error}
        />
        {status && (
          <Box mt={1}>
            <Typography
              variant="body2"
              style={{
                color: severityColour(
                  status.enabled === true
                    ? 'success'
                    : status.enabled === false
                    ? 'error'
                    : 'warning'
                ).fg,
              }}>
              Current status: {status.text}
            </Typography>
          </Box>
        )}
        {info && (
          <Box mt={2}>
            <Alert severity="info">{info}</Alert>
          </Box>
        )}
        {READ_ONLY && (
          <Box mt={2}>
            <Typography variant="body2" color="primary">
              Production: enable this flag from Dashboard → Feature Management.
            </Typography>
          </Box>
        )}
        <Box mt={3}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={submitting || formLocked}>
            Enable Feature
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default EnableLegacySchoolFallback;
